import { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { Text, TextBlock } from '../../Atoms/Text';
import { SearchBox } from '../../Compounds/Search/Search';
import { EntityType, SearchResult } from '../../../store/Search';
import { Adjustment, createAdjustmentTag, nextAdjustmentDetails } from '../../../store/Adjustment';
import { useSnackbar } from '../../../Context/SnackbarContext';

interface NewAdjustmentTagDialogProps {
  open: boolean;
  onClose: () => void;
  adjustment: Adjustment;
}

interface FormData {
  assetUpstackId: string | null;
  note: string | null;
  tallyRunId: number;
  commissionAdjustmentId: number;
}

export const NewAdjustmentTagDialog = ({ open, onClose, adjustment }: NewAdjustmentTagDialogProps) => {
  const isNextAdjustmentPresent = !!nextAdjustmentDetails(adjustment, false);
  const defaultFormData: FormData = {
    assetUpstackId: null,
    note: null,
    tallyRunId: adjustment.tallyRunId,
    commissionAdjustmentId: adjustment.commissionAdjustmentId
  };
  const [tagAdjustment, setTagAdjustment] = useState<FormData>(defaultFormData);

  const [loading, setLoading] = useState<boolean>(false);
  const { setSnack } = useSnackbar();

  const resetForm = () => {
    setTagAdjustment(defaultFormData);
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, adjustment.commissionAdjustmentId]);

  const handleSelect = (result: SearchResult) => {
    if (!result || result.source !== EntityType.ASSET) return;
    setTagAdjustment({ ...tagAdjustment, assetUpstackId: result.id });
  };

  const canSave = !!tagAdjustment.assetUpstackId && !loading;

  const handleSave = async (close = true) => {
    if (!canSave) return;
    setLoading(true);
    tagAdjustment.note = tagAdjustment.note?.trim() || null;
    const result = await createAdjustmentTag(tagAdjustment);
    setLoading(false);

    if (result?.error) return;

    setSnack({ message: 'Adjustment tag created successfully', type: 'success', open: true });
    if (close) {
      onClose();
    } else {
      nextAdjustmentDetails(adjustment);
      resetForm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          },
          sx: { overflowY: 'visible', maxWidth: 750, padding: 1 }
        }
      }}
      data-testid="new-adjustment-tag-modal">
      <DialogTitle>
        <Text
          weight="bold"
          size="lg"
          color="grey9">
          New Adjustment Tag
        </Text>
      </DialogTitle>

      <DialogContent
        sx={{ pb: 2, display: 'flex', flexDirection: 'column', gap: 2, px: 6 }}
        key={adjustment.commissionAdjustmentId}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ width: 1 / 2 }}>
            <TextBlock
              size="sm14"
              color="grey6"
              className="mb-2">
              Adjustment
            </TextBlock>
            <TextBlock
              size="sm14"
              className="mb-2">
              Adj ID: {adjustment.commissionAdjustmentId}
            </TextBlock>
          </Box>
          <Box sx={{ width: 1 / 2 }}>
            <TextBlock
              size="sm14"
              color="grey6"
              className="mb-2">
              Asset
            </TextBlock>
            <SearchBox
              fromModal
              fullWidth
              inputString=""
              handleSelect={handleSelect}
              handleClearSelect={() => setTagAdjustment({ ...tagAdjustment, assetUpstackId: null })}
              searchEntityType={EntityType.ASSET}
              dropdownZIndex={1400}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box sx={{ width: 1 / 2 }}>
            <Text
              size="sm14"
              color="grey6"
              className="mt-4 mb-2">
              Note
            </Text>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={tagAdjustment.note}
              onChange={(e) => setTagAdjustment({ ...tagAdjustment, note: e.target.value })}
              slotProps={{
                htmlInput: { 'data-testid': 'new-adjustment-note-input' }
              }}
            />
          </Box>
          <Box sx={{ width: 1 / 2 }} />
        </Box>
      </DialogContent>
      <hr className="my-1 -mx-2" />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          data-testid="new-adjustment-cancel-button"
          onClick={onClose}>
          Cancel
        </Button>
        <Button
          data-testid="new-adjustment-save-next-button"
          disabled={!canSave || !isNextAdjustmentPresent}
          onClick={() => handleSave(false)}>
          Save and show Next
        </Button>
        <Button
          data-testid="new-adjustment-save-button"
          disabled={!canSave}
          variant="contained"
          type="submit">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
